// Sprint 3: la conversazione vera con streaming SSE.
//
// Stato AudiqMark:
//  - 'idle' → in attesa input utente
//  - 'thinking' → turno inviato, aspettiamo il primo text_delta
//  - 'speaking' → durante streaming dei text_delta
//  - 'done' → 1s dopo final, poi torna a idle
//
// Resume: al mount, se sessionId è in URL, GET /api/session/:id e ricostruisce
// la history. Se status terminale, mostra schermata di chiusura.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AudiqMark, AudiqWordmark, ThemeToggle, RuleBicolor, type AudiqMarkState } from '@audiq/ui';
import { LangSwitcher } from '../components/LangSwitcher';
import { MicButton } from '../components/MicButton';
import { IdentityAtEndForm } from '../components/IdentityAtEndForm';
import {
  getActiveFlow,
  resumeSession,
  streamTurn,
  streamVoiceTurn,
  ApiError,
  type ResumedSession,
} from '../lib/api';
import { usePushToTalk } from '../lib/usePushToTalk';
import { VoicePlayback } from '../lib/voicePlayback';
import { applyDirAndFontForLang } from '../i18n';

interface UiBubble {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  streaming?: boolean;
  /** Sprint 8: trascrizione audio del messaggio user (mostrata sopra la bolla) */
  audioTranscript?: { duration_seconds: number; language_detected: string } | null;
}

let bubbleSeq = 0;
function nextBubbleId() {
  bubbleSeq += 1;
  return `b-${bubbleSeq}-${Date.now()}`;
}

export function ChatPage() {
  const { t } = useTranslation();
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [bubbles, setBubbles] = useState<UiBubble[]>([]);
  const [markState, setMarkState] = useState<AudiqMarkState>('idle');
  const [input, setInput] = useState('');
  const [resumed, setResumed] = useState<ResumedSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [turnError, setTurnError] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);
  const [emergency, setEmergency] = useState<{ contact_text: string } | null>(null);
  const [outcomes, setOutcomes] = useState<
    Array<{ id: string; name: string; description: string }>
  >([]);
  const [completed, setCompleted] = useState(false);
  const [identitySubmitted, setIdentitySubmitted] = useState(false);
  const flowQuery = useQuery({ queryKey: ['flow-active'], queryFn: getActiveFlow });
  // Sprint 8: voice mode (text|voice). Default 'text'; può essere voice se l'utente
  // ha scelto in StartPage e voice_config è enabled lato server.
  const [chatMode] = useState<'text' | 'voice'>(
    () => (sessionStorage.getItem('audiq-chat-mode') as 'text' | 'voice') ?? 'text',
  );
  const [audioFallback, setAudioFallback] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const playbackRef = useRef<VoicePlayback | null>(null);
  if (!playbackRef.current) playbackRef.current = new VoicePlayback();

  // -------- 1. Resume on mount --------
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await resumeSession(sessionId);
        if (cancelled) return;
        setResumed(data);
        if (data.identity_collected) setIdentitySubmitted(true);
        setBubbles(
          data.messages
            .filter((m) => m.role !== 'system')
            .map((m) => ({
              id: nextBubbleId(),
              role: m.role as 'user' | 'assistant',
              content: m.content,
              timestamp: new Date(m.timestamp).getTime(),
            })),
        );
        setOutcomes(data.outcomes_presented);
        if (data.status !== 'in_progress') {
          if (data.status === 'aborted_emergency' || data.emergency_triggered) {
            setEmergency({ contact_text: t('chat.emergencyTitle') });
          } else {
            setCompleted(true);
          }
        }
        if (data.language_detected) {
          applyDirAndFontForLang(data.language_detected);
        }
      } catch (err) {
        if (cancelled) return;
        const msg =
          err instanceof ApiError && err.code === 'SESSION_NOT_FOUND'
            ? t('chat.loadError')
            : t('chat.loadError');
        setLoadError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, t]);

  // -------- 2. Auto-scroll on bubble update --------
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [bubbles, streaming]);

  // -------- 3. Send turn --------
  const sendTurn = useCallback(
    async (message: string) => {
      if (!sessionId || streaming || completed || emergency) return;
      const userBubble: UiBubble = {
        id: nextBubbleId(),
        role: 'user',
        content: message,
        timestamp: Date.now(),
      };
      const assistantBubble: UiBubble = {
        id: nextBubbleId(),
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
        streaming: true,
      };
      setBubbles((prev) => [...prev, userBubble, assistantBubble]);
      setInput('');
      setTurnError(null);
      setMarkState('thinking');
      setStreaming(true);

      try {
        let firstDeltaSeen = false;
        for await (const ev of streamTurn(sessionId, message)) {
          if (ev.type === 'text_delta') {
            if (!firstDeltaSeen) {
              firstDeltaSeen = true;
              setMarkState('speaking');
            }
            setBubbles((prev) =>
              prev.map((b) =>
                b.id === assistantBubble.id ? { ...b, content: b.content + ev.text } : b,
              ),
            );
          } else if (ev.type === 'final') {
            setBubbles((prev) =>
              prev.map((b) => (b.id === assistantBubble.id ? { ...b, streaming: false } : b)),
            );
            if (ev.status === 'completed') {
              setCompleted(true);
            } else if (ev.status === 'aborted_emergency') {
              // l'evento 'emergency' è arrivato già prima del final
            }
          } else if (ev.type === 'outcomes') {
            setOutcomes(
              ev.outcomes.map((o) => ({
                id: o.id,
                name: o.name,
                description: o.description,
              })),
            );
          } else if (ev.type === 'emergency') {
            setEmergency({ contact_text: ev.contact_text });
          } else if (ev.type === 'error') {
            setTurnError(t('chat.turnError'));
          }
        }
        setMarkState('done');
        window.setTimeout(() => setMarkState('idle'), 1000);
      } catch (err) {
        setTurnError(err instanceof ApiError ? err.message : t('chat.turnError'));
        setMarkState('idle');
        // Marca l'ultima bubble assistant streaming come failed (rimuovi)
        setBubbles((prev) => prev.filter((b) => b.id !== assistantBubble.id));
      } finally {
        setStreaming(false);
      }
    },
    [sessionId, streaming, completed, emergency, t],
  );

  // -------- 4. Voice turn (Sprint 8) --------
  const sendVoiceTurn = useCallback(
    async (audioBlob: Blob) => {
      if (!sessionId || streaming || completed || emergency) return;
      // Inizializza AudioContext durante user gesture (mousedown del MicButton già stato fatto)
      playbackRef.current?.ensureContext();
      // Pre-create user bubble (poi popoliamo con transcript dall'evento 'transcription')
      const userBubble: UiBubble = {
        id: nextBubbleId(),
        role: 'user',
        content: '...',
        timestamp: Date.now(),
        audioTranscript: { duration_seconds: 0, language_detected: '' },
      };
      const assistantBubble: UiBubble = {
        id: nextBubbleId(),
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
        streaming: true,
      };
      setBubbles((prev) => [...prev, userBubble, assistantBubble]);
      setTurnError(null);
      setAudioFallback(null);
      setMarkState('thinking');
      setStreaming(true);

      try {
        let firstDeltaSeen = false;
        for await (const ev of streamVoiceTurn(sessionId, audioBlob)) {
          if (ev.type === 'transcription') {
            setBubbles((prev) =>
              prev.map((b) =>
                b.id === userBubble.id
                  ? {
                      ...b,
                      content: ev.text,
                      audioTranscript: {
                        duration_seconds: ev.duration_seconds,
                        language_detected: ev.language_detected,
                      },
                    }
                  : b,
              ),
            );
            applyDirAndFontForLang(ev.language_detected);
          } else if (ev.type === 'text_delta') {
            if (!firstDeltaSeen) {
              firstDeltaSeen = true;
              setMarkState('speaking');
            }
            setBubbles((prev) =>
              prev.map((b) =>
                b.id === assistantBubble.id ? { ...b, content: b.content + ev.text } : b,
              ),
            );
          } else if (ev.type === 'final') {
            setBubbles((prev) =>
              prev.map((b) => (b.id === assistantBubble.id ? { ...b, streaming: false } : b)),
            );
            if (ev.status === 'completed') {
              setCompleted(true);
            }
          } else if (ev.type === 'outcomes') {
            setOutcomes(
              ev.outcomes.map((o) => ({ id: o.id, name: o.name, description: o.description })),
            );
          } else if (ev.type === 'emergency') {
            setEmergency({ contact_text: ev.contact_text });
          } else if (ev.type === 'audio_chunk') {
            // Accumula chunks mp3 nel playback
            void playbackRef.current?.pushChunk(ev.data);
          } else if (ev.type === 'audio_end') {
            void playbackRef.current?.finalize();
          } else if (ev.type === 'audio_error') {
            setAudioFallback(ev.message);
          } else if (ev.type === 'error') {
            setTurnError(t('chat.turnError'));
          }
        }
        setMarkState('done');
        window.setTimeout(() => setMarkState('idle'), 1000);
      } catch (err) {
        if (err instanceof ApiError && err.code === 'AUDIO_CONSENT_MISSING') {
          setTurnError(
            t('chat.voiceConsentMissing', {
              defaultValue:
                "Manca il consenso audio: torna all'inizio e accetta il trattamento voce.",
            }),
          );
        } else if (err instanceof ApiError && err.code === 'AUDIO_CAP_EXCEEDED') {
          setTurnError(
            t('chat.voiceCapExceeded', {
              defaultValue: 'Limite mensile audio raggiunto. Continua in modalità testo.',
            }),
          );
        } else if (err instanceof ApiError && err.code === 'AUDIO_NO_SPEECH') {
          setTurnError(
            t('chat.voiceNoSpeech', {
              defaultValue: "Non ho capito. Riprova un po' più forte o vicino al microfono.",
            }),
          );
        } else {
          setTurnError(err instanceof ApiError ? err.message : t('chat.turnError'));
        }
        setMarkState('idle');
        setBubbles((prev) =>
          prev.filter((b) => b.id !== assistantBubble.id && b.id !== userBubble.id),
        );
      } finally {
        setStreaming(false);
      }
    },
    [sessionId, streaming, completed, emergency, t],
  );

  const ptt = usePushToTalk({
    onAudioReady: sendVoiceTurn,
    disabled: streaming || completed || !!emergency || chatMode !== 'voice',
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    void sendTurn(text);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter invia, Shift+Enter newline
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const text = input.trim();
      if (text) void sendTurn(text);
    }
  };

  const placeholderShort = useMemo(() => sessionId?.slice(0, 8) ?? '', [sessionId]);

  if (loading) {
    return (
      <FullScreen>
        <div className="flex flex-col items-center gap-4">
          <AudiqMark size={64} state="thinking" />
          <p className="mono text-sm" style={{ color: 'var(--mute)' }}>
            {t('chat.loadingResume')}
          </p>
        </div>
      </FullScreen>
    );
  }

  if (loadError) {
    return (
      <FullScreen>
        <div className="max-w-md text-center">
          <p style={{ color: 'var(--danger)' }}>{loadError}</p>
          <Link
            to="/"
            className="mono mt-4 inline-block underline"
            style={{ color: 'var(--mute)' }}
          >
            {t('common.back')}
          </Link>
        </div>
      </FullScreen>
    );
  }

  return (
    <div
      className="flex flex-1 flex-col"
      style={{ background: 'var(--surface)', color: 'var(--ink)' }}
    >
      <header
        className="sticky top-0 z-10 flex items-center justify-between border-b px-4 py-3 backdrop-blur"
        style={{ background: 'var(--surface)', borderColor: 'var(--line)' }}
      >
        <div className="flex items-center gap-3">
          <AudiqMark size={36} state={markState} />
          <AudiqWordmark size="sm" />
        </div>
        <div className="flex items-center gap-2">
          <LangSwitcher />
          <ThemeToggle size="sm" />
        </div>
      </header>

      <main ref={scrollRef} className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto px-4 py-6">
        {bubbles.map((b) => (
          <ChatBubble key={b.id} bubble={b} />
        ))}

        {emergency ? (
          <EmergencyBanner
            contactText={emergency.contact_text}
            onAck={() => navigate(`/done/${resumed?.pickup_code ?? ''}`)}
            ackLabel={t('chat.emergencyAck')}
            title={t('chat.emergencyTitle')}
          />
        ) : null}

        {completed && outcomes.length > 0 ? (
          <OutcomesCard outcomes={outcomes} title={t('chat.outcomesTitle')} />
        ) : null}

        {completed &&
        sessionId &&
        flowQuery.data?.settings?.ask_personal_info?.enabled &&
        flowQuery.data.settings.ask_personal_info.timing === 'at_end' &&
        !identitySubmitted ? (
          <IdentityAtEndForm
            sessionId={sessionId}
            fields={flowQuery.data.settings.ask_personal_info.fields}
            required={flowQuery.data.settings.ask_personal_info.required}
            onDone={() => setIdentitySubmitted(true)}
          />
        ) : completed ? (
          <CompletedFooter
            pickupCode={resumed?.pickup_code ?? ''}
            navigate={navigate}
            title={t('chat.completedTitle')}
            subtitle={t('chat.completedSubtitle')}
            cta={t('chat.completedDone')}
          />
        ) : null}

        {turnError ? (
          <p className="mt-3 text-sm" style={{ color: 'var(--danger)' }} role="alert">
            {turnError}
          </p>
        ) : null}
      </main>

      {!completed && !emergency ? (
        chatMode === 'voice' ? (
          <div
            className="border-t px-4 py-6"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <div className="mx-auto flex max-w-2xl flex-col items-center gap-2">
              <MicButton
                state={ptt.state}
                recordingMs={ptt.recordingMs}
                error={ptt.error}
                onStart={ptt.start}
                onStop={ptt.stop}
                onReset={ptt.reset}
                disabled={streaming}
              />
              {audioFallback ? (
                <p className="mono text-xs" style={{ color: 'var(--mute)' }}>
                  {t('chat.voiceAudioFallback', {
                    defaultValue:
                      "L'audio della risposta non è disponibile, ma il testo è arrivato regolarmente.",
                  })}
                </p>
              ) : null}
              <div className="mono mt-1 text-center text-xs" style={{ color: 'var(--mute)' }}>
                session · {placeholderShort}…
              </div>
            </div>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="border-t px-4 py-3"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <div className="mx-auto flex max-w-2xl items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={t('chat.inputPlaceholder')}
                rows={1}
                disabled={streaming}
                maxLength={2000}
                className="flex-1 resize-none rounded-md border bg-transparent px-3 py-2 text-base outline-none disabled:opacity-50"
                style={{
                  borderColor: 'var(--line)',
                  color: 'var(--ink)',
                  fontFamily: 'var(--font-sans)',
                  minHeight: '2.5rem',
                  maxHeight: '8rem',
                }}
              />
              <button
                type="submit"
                disabled={streaming || input.trim().length === 0}
                className="rounded-md px-4 py-2 text-sm font-semibold transition disabled:opacity-50"
                style={{ background: 'var(--primary)', color: '#fff' }}
              >
                {t('chat.send')}
              </button>
            </div>
            <div className="mono mt-1 text-center text-xs" style={{ color: 'var(--mute)' }}>
              session · {placeholderShort}…
            </div>
          </form>
        )
      ) : null}
    </div>
  );
}

// ====================================================================
// Sotto-componenti
// ====================================================================

function FullScreen({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="flex flex-1 items-center justify-center px-6"
      style={{ background: 'var(--surface)', color: 'var(--ink)' }}
    >
      {children}
    </div>
  );
}

function ChatBubble({ bubble }: { bubble: UiBubble }) {
  const isUser = bubble.role === 'user';
  return (
    <div className={`mb-3 flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
      {isUser && bubble.audioTranscript ? (
        <span
          className="mono mb-1 text-[10px] uppercase tracking-wide"
          style={{ color: 'var(--mute)' }}
        >
          🎤 {bubble.audioTranscript.duration_seconds.toFixed(1)}s
          {bubble.audioTranscript.language_detected
            ? ` · ${bubble.audioTranscript.language_detected}`
            : ''}
        </span>
      ) : null}
      <div
        className="max-w-[85%] rounded-2xl px-4 py-2.5 text-base"
        style={{
          background: isUser ? 'var(--primary)' : 'var(--paper)',
          color: isUser ? '#fff' : 'var(--ink)',
          border: isUser ? 'none' : '1px solid var(--line)',
          fontFamily: 'var(--font-sans)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {bubble.content}
        {bubble.streaming ? <span className="ms-1 inline-block animate-pulse">▍</span> : null}
      </div>
    </div>
  );
}

function EmergencyBanner({
  contactText,
  onAck,
  ackLabel,
  title,
}: {
  contactText: string;
  onAck: () => void;
  ackLabel: string;
  title: string;
}) {
  return (
    <div
      role="alert"
      className="mt-4 rounded-md border-l-4 px-4 py-3"
      style={{
        background: 'var(--paper)',
        borderColor: 'var(--danger)',
        color: 'var(--ink)',
      }}
    >
      <p className="mb-1 font-semibold" style={{ color: 'var(--danger)' }}>
        {title}
      </p>
      <p className="text-sm">{contactText}</p>
      <button
        onClick={onAck}
        className="mt-3 rounded-md px-3 py-1.5 text-sm"
        style={{ background: 'var(--danger)', color: '#fff' }}
      >
        {ackLabel}
      </button>
    </div>
  );
}

function OutcomesCard({
  outcomes,
  title,
}: {
  outcomes: Array<{ id: string; name: string; description: string }>;
  title: string;
}) {
  return (
    <div
      className="mt-6 rounded-md border p-4"
      style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
    >
      <h3 className="mono mb-3 text-xs" style={{ color: 'var(--mute)' }}>
        {title}
      </h3>
      <ul className="space-y-3">
        {outcomes.map((o) => (
          <li key={o.id}>
            <div className="font-semibold" style={{ color: 'var(--ink)' }}>
              {o.name}
            </div>
            {o.description ? (
              <div className="text-sm" style={{ color: 'var(--mute)' }}>
                {o.description}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CompletedFooter({
  pickupCode,
  navigate,
  title,
  subtitle,
  cta,
}: {
  pickupCode: string;
  navigate: ReturnType<typeof useNavigate>;
  title: string;
  subtitle: string;
  cta: string;
}) {
  return (
    <div className="mt-6 text-center">
      <RuleBicolor className="mx-auto mb-4 max-w-[8rem]" />
      <h2 className="serif text-2xl" style={{ color: 'var(--ink)' }}>
        {title}
      </h2>
      <p className="mt-2 text-sm" style={{ color: 'var(--mute)' }}>
        {subtitle}
      </p>
      <button
        onClick={() => navigate(`/done/${pickupCode}`)}
        className="mt-4 rounded-md px-5 py-2.5 text-sm font-semibold"
        style={{ background: 'var(--primary)', color: '#fff' }}
      >
        {cta}
      </button>
    </div>
  );
}
